import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getEmpresas, createEmpresa, updateEmpresa, deleteEmpresa, duplicarEmpresa } from "@/lib/api";
import { notifyDataChanged } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Trash2, Pencil, Users, Copy, Loader2 } from "lucide-react";

const emptyEmpresa = { nombre: "", nif: "", direccion: "", telefono: "", email: "" };

export default function EmpresasPage() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [formData, setFormData] = useState(emptyEmpresa);
  // Duplicar (propia o compartida → copia mía privada)
  const [duplicarTarget, setDuplicarTarget] = useState(null);
  const [duplicarNombre, setDuplicarNombre] = useState("");
  const [duplicando, setDuplicando] = useState(false);

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      const response = await getEmpresas();
      setEmpresas(response.data);
    } catch (error) {
      toast.error("Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingEmpresa(null);
    setFormData(emptyEmpresa);
    setDialogOpen(true);
  };

  const handleOpenEdit = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombre: empresa.nombre || "",
      nif: empresa.nif || "",
      direccion: empresa.direccion || "",
      telefono: empresa.telefono || "",
      email: empresa.email || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    try {
      if (editingEmpresa) {
        await updateEmpresa(editingEmpresa.id, formData);
        toast.success("Empresa actualizada correctamente");
      } else {
        await createEmpresa(formData);
        toast.success("Empresa creada correctamente");
      }
      setDialogOpen(false);
      setFormData(emptyEmpresa);
      setEditingEmpresa(null);
      loadEmpresas();
      notifyDataChanged("empresas");
    } catch (error) {
      toast.error(editingEmpresa ? "Error al actualizar empresa" : "Error al crear empresa");
    }
  };

  const handleOpenDelete = (empresa) => {
    setEmpresaToDelete(empresa);
    setDeleteDialogOpen(true);
  };

  const handleOpenDuplicar = (empresa) => {
    setDuplicarTarget(empresa);
    setDuplicarNombre(`${empresa.nombre} (copia)`);
  };

  const handleDuplicar = async () => {
    if (!duplicarTarget) return;
    setDuplicando(true);
    try {
      const { data } = await duplicarEmpresa(duplicarTarget.id, duplicarNombre.trim() || null);
      const r = data.resumen || {};
      toast.success(
        `«${data.nombre}» creada: ${r.ejercicios || 0} ejercicios, ${r.cuentas || 0} cuentas` +
        (r.holded_apuntes ? `, ${r.holded_apuntes.toLocaleString("es-ES")} apuntes` : "")
      );
      (data.avisos || []).forEach((a) => toast.message(a));
      setDuplicarTarget(null);
      loadEmpresas();
      notifyDataChanged("empresas");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al duplicar la empresa");
    } finally {
      setDuplicando(false);
    }
  };

  const handleDelete = async () => {
    if (!empresaToDelete) return;
    
    try {
      await deleteEmpresa(empresaToDelete.id);
      toast.success("Empresa eliminada");
      setDeleteDialogOpen(false);
      setEmpresaToDelete(null);
      loadEmpresas();
      notifyDataChanged("empresas");
    } catch (error) {
      toast.error("Error al eliminar empresa");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Empresas</h1>
          <p className="text-muted-foreground mt-1">
            Gestión de empresas y sus datos básicos
          </p>
        </div>
        <Button onClick={handleOpenCreate} data-testid="create-empresa-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Empresa
        </Button>
      </div>

      {/* Empty State */}
      {empresas.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No hay empresas</h3>
            <p className="text-muted-foreground mb-4 text-center max-w-sm">
              Crea tu primera empresa para empezar a gestionar sus estados financieros según el PGC Pymes
            </p>
            <Button onClick={handleOpenCreate} data-testid="create-first-empresa-btn">
              <Plus className="h-4 w-4 mr-2" />
              Crear Primera Empresa
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Empresas Table */
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Nombre</TableHead>
                  <TableHead className="font-semibold">CIF</TableHead>
                  <TableHead className="font-semibold">Dirección</TableHead>
                  <TableHead className="font-semibold">Teléfono</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empresas.map((empresa) => {
                  const isOwner = (empresa.my_role || "owner") === "owner";
                  return (
                    <TableRow key={empresa.id} data-testid={`empresa-row-${empresa.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{empresa.nombre}</span>
                          {!isOwner && (
                            <Badge variant="outline" className="text-xs">
                              <Users className="h-3 w-3 mr-1" />
                              Compartida ({empresa.my_role === "write" ? "edición" : "lectura"})
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{empresa.nif || "-"}</TableCell>
                      <TableCell className="text-sm">{empresa.direccion || "-"}</TableCell>
                      <TableCell className="text-sm">{empresa.telefono || "-"}</TableCell>
                      <TableCell className="text-sm">{empresa.email || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Duplicar: vale para propias (sandbox) y compartidas
                              ("duplícamela a mí") — la copia siempre es del usuario. */}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Duplicar (crea una copia tuya, privada)"
                            onClick={() => handleOpenDuplicar(empresa)}
                            data-testid={`duplicar-empresa-${empresa.id}`}
                          >
                            <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>
                          {isOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEdit(empresa)}
                              data-testid={`edit-empresa-${empresa.id}`}
                            >
                              <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                            </Button>
                          )}
                          {isOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDelete(empresa)}
                              data-testid={`delete-empresa-${empresa.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Duplicar Dialog */}
      <Dialog open={!!duplicarTarget} onOpenChange={(o) => !o && setDuplicarTarget(null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Duplicar «{duplicarTarget?.nombre}»</DialogTitle>
            <DialogDescription>
              Se crea una copia completa <strong>tuya y privada</strong> (ejercicios,
              cuentas, proyección, diario…), sin vínculo con la original
              {duplicarTarget?.my_role && duplicarTarget.my_role !== "owner"
                ? " ni con quien te la compartió"
                : ""}. La copia nace <strong>sin NIF</strong> (evita que un export a
              Finoview pise a la original) y sin la conexión de Holded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="duplicar-nombre">Nombre de la copia</Label>
            <Input
              id="duplicar-nombre"
              value={duplicarNombre}
              onChange={(e) => setDuplicarNombre(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !duplicando && handleDuplicar()}
              data-testid="duplicar-nombre-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicarTarget(null)} disabled={duplicando}>
              Cancelar
            </Button>
            <Button onClick={handleDuplicar} disabled={duplicando} data-testid="duplicar-confirmar-btn">
              {duplicando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
              {duplicando ? "Duplicando…" : "Duplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingEmpresa ? "Editar Empresa" : "Crear Nueva Empresa"}
            </DialogTitle>
            <DialogDescription>
              {editingEmpresa 
                ? "Modifica los datos de la empresa"
                : "Añade una nueva empresa para gestionar sus estados financieros"
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="nombre">Nombre de la empresa *</Label>
                <Input
                  id="nombre"
                  data-testid="empresa-nombre-input"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Mi Empresa S.L."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nif">NIF / CIF</Label>
                <Input
                  id="nif"
                  data-testid="empresa-nif-input"
                  value={formData.nif}
                  onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                  placeholder="Ej: B12345678"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  data-testid="empresa-telefono-input"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="Ej: 912345678"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="empresa-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Ej: contacto@miempresa.com"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  data-testid="empresa-direccion-input"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Ej: Calle Principal 123, Madrid"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-empresa-btn">
                {editingEmpresa ? "Guardar Cambios" : "Crear Empresa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán todos los ejercicios y datos asociados a <strong>{empresaToDelete?.nombre}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
